import { useEffect, useRef, useState, useCallback, useMemo, type RefCallback } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { DialogBox } from '../Dialog/DialogBox';

import type { WizardStepProps } from './types';
import styles from './wizard.module.scss';

type StepWrapperState = 'opening' | 'open' | 'closing' | 'closed';

export const StepWrapper = observer(function StepWrapper(props: WizardStepProps) {
  const { setHeight, isCurrent, children } = props;

  const elementRef = useRef<HTMLDivElement | null>(null);

  const [state, setState] = useState<StepWrapperState>(() => (isCurrent ? 'open' : 'closed'));

  useEffect(() => {
    setState((prevState) => {
      if (isCurrent) {
        return prevState === 'open' ? prevState : 'opening';
      }
      return prevState === 'closed' ? prevState : 'closing';
    });
  }, [isCurrent]);

  const handleTransitionEnd = useCallback(
    () =>
      setState((prevState) => {
        switch (prevState) {
          case 'closing':
            return 'closed';
          case 'opening':
            return 'open';
          default:
            return prevState;
        }
      }),
    [],
  );

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries, observerInstance) => {
        let observed = false;

        entries.forEach((entry) => {
          if (observed || entry.target !== elementRef.current) {
            observerInstance.unobserve(entry.target);
          } else {
            observed = true;
            setHeight(entry.contentRect.height);
          }
        });
      }),
    [setHeight],
  );

  const ref = useCallback<RefCallback<HTMLDivElement>>(
    (instance) => {
      const prevElement = elementRef.current;

      if (prevElement) {
        resizeObserver.unobserve(prevElement);
      }

      if (!isCurrent) {
        elementRef.current = null;

        return;
      }

      elementRef.current = instance;

      if (instance) {
        resizeObserver.observe(instance);
      }
    },
    [isCurrent, resizeObserver],
  );

  return (
    <DialogBox
      ref={ref}
      className={classNames(
        styles.wizardStep,
        'wizard_step_overrideable',
        {
          [styles.visibleStep]: isCurrent,
          [styles.invisible]: state === 'closed',
        },
        props.containerClassName,
      )}
      onTransitionEnd={handleTransitionEnd}
    >
      {children}
    </DialogBox>
  );
});
